<template>
  <div>
    <LabelledInput
      label="Font size"
      helpful-tip="Percentage value to scale font size up or down relative to other story panels"
    >
      <b-input-group append="%">
        <b-input
          v-model="fontSize"
          type="number"
          min="50"
          max="500"
          step="10"
        />
      </b-input-group>
    </LabelledInput>

    <LabelledInput
      label="Font colour"
      helpful-tip="Set the font colour. Used for paragraph text and by default for heading and links."
    >
      <ColorPicker
        v-model="fontColor"
        data-cy="font-color"
        :debounce="500"
      />
    </LabelledInput>

    <LabelledInput
      label="Font family"
      helpful-tip="If your preferred font is not available, please contact OrbViz"
    >
      <FontPicker
        v-model="fontFamily"
        data-cy="font-family"
      />
    </LabelledInput>

    <LabelledInput
      label="Heading colour"
      helpful-tip="Make headings a different colour to paragraphs"
    >
      <ColorPicker
        v-model="headingColor"
        :placeholder="fontColor"
        :debounce="500"
      />
    </LabelledInput>

    <LabelledInput
      label="Link colour"
      helpful-tip="Make links a different colour to paragraphs"
    >
      <ColorPicker
        v-model="linkColor"
        :placeholder="fontColor"
        :debounce="500"
      />
    </LabelledInput>

    <LabelledInput
      label="Table border colour"
      helpful-tip="Make the table border a different colour to paragraphs"
    >
      <ColorPicker
        v-model="tableBorderColor"
        :placeholder="fontColor"
        :debounce="500"
      />
    </LabelledInput>
  </div>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import { ColorPicker } from '@orbica/vue-modules'
import FontPicker from '@/components/builder/StorySettings/FontPicker.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'

import { StorySettingsConfig, StorySetting } from '@/types'
import { STORY_SETTINGS_CONFIG_KEY } from '@/constants'

const components = {
  ColorPicker,
  FontPicker,
  LabelledInput,
}

const props = {
  panel: {
    type: String,
    required: true,
  },
}

@Component({ components })
export default class PanelSettings extends Vue.extend({ props }) {
  get fontSize () {
    return this.$typedStore.primary.app.config.storySettings[this.panel as keyof StorySettingsConfig]?.fontSize
  }

  set fontSize (value) {
    this.updateStorySettingsConfig(value, 'fontSize')
  }

  get fontColor () {
    return this.$typedStore.primary.app.config.storySettings[this.panel as keyof StorySettingsConfig]?.fontColor
  }

  set fontColor (value) {
    this.updateStorySettingsConfig(value, 'fontColor')
  }

  get fontFamily () {
    return this.$typedStore.primary.app.config.storySettings[this.panel as keyof StorySettingsConfig]?.fontFamily || ''
  }

  set fontFamily (value) {
    this.updateStorySettingsConfig(value, 'fontFamily')
  }

  get headingColor () {
    return this.$typedStore.primary.app.config.storySettings[this.panel as keyof StorySettingsConfig]?.headingColor
  }

  set headingColor (value) {
    this.updateStorySettingsConfig(value, 'headingColor')
  }

  get linkColor () {
    return this.$typedStore.primary.app.config.storySettings[this.panel as keyof StorySettingsConfig]?.linkColor
  }

  set linkColor (value) {
    this.updateStorySettingsConfig(value, 'linkColor')
  }

  get tableBorderColor () {
    return this.$typedStore.primary.app.config.storySettings[this.panel as keyof StorySettingsConfig]?.tableBorderColor
  }

  set tableBorderColor (value) {
    this.updateStorySettingsConfig(value, 'tableBorderColor')
  }

  updateStorySettingsConfig (value: unknown, ...args: [keyof StorySetting]) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [STORY_SETTINGS_CONFIG_KEY, this.panel, ...args],
      value,
    })
  }
}

</script>
